/** Pause session for Q&A — trainer only (from Figma StartQaPauseModal). */

import React, { useEffect, useState } from "react";
import { CircleHelp, Clock } from "lucide-react";

const QA_DURATION_PRESETS = [5, 10, 15, 20, 30, 45] as const;

type StartQaPauseModalProps = {
  open: boolean;
  defaultDurationMinutes?: number;
  onClose: () => void;
  onStart: (durationMinutes: number) => void;
};

export function StartQaPauseModal({
  open,
  defaultDurationMinutes = 10,
  onClose,
  onStart,
}: StartQaPauseModalProps) {
  const [duration, setDuration] = useState(defaultDurationMinutes);

  useEffect(() => {
    if (open) setDuration(defaultDurationMinutes);
  }, [open, defaultDurationMinutes]);

  if (!open) return null;

  const clamp = (n: number) => Math.min(60, Math.max(5, Math.round(n) || 5));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="qa-pause-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(4px)",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 16,
          border: "1px solid rgba(255,255,255,0.1)",
          background: "#1a1d26",
          padding: 20,
          boxShadow: "0 24px 48px rgba(0,0,0,0.5)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <CircleHelp size={20} color="#fbbf24" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <h2
              id="qa-pause-title"
              style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#fff" }}
            >
              Pause session for Q&A
            </h2>
            <p style={{ margin: "6px 0 0", fontSize: 13, color: "#94a3b8", lineHeight: 1.45 }}>
              The session timer and topic progress will pause while you answer
              student questions.
            </p>
          </div>
        </div>

        <div style={{ marginTop: 20 }}>
          <label
            htmlFor="qa-duration"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 8,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#64748b",
            }}
          >
            <Clock size={14} />
            Q&A duration
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {QA_DURATION_PRESETS.map((minutes) => (
              <button
                key={minutes}
                type="button"
                onClick={() => setDuration(minutes)}
                style={{
                  borderRadius: 8,
                  padding: "8px 12px",
                  border: "none",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  background: duration === minutes ? "#d97706" : "rgba(255,255,255,0.05)",
                  color: duration === minutes ? "#fff" : "#cbd5e1",
                }}
              >
                {minutes} min
              </button>
            ))}
          </div>
          <input
            id="qa-duration"
            type="number"
            min={5}
            max={60}
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value) || 5)}
            style={{
              marginTop: 12,
              width: "100%",
              boxSizing: "border-box",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.1)",
              background: "#141820",
              padding: "8px 12px",
              fontSize: 13,
              color: "#fff",
            }}
          />
        </div>

        <div
          style={{
            marginTop: 24,
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.1)",
              background: "transparent",
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 600,
              color: "#cbd5e1",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onStart(clamp(duration));
              onClose();
            }}
            style={{
              borderRadius: 8,
              border: "none",
              background: "#d97706",
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 600,
              color: "#fff",
              cursor: "pointer",
            }}
          >
            Pause & start Q&A
          </button>
        </div>
      </div>
    </div>
  );
}
